/**
 * Cómo se dice cada estado en pantalla.
 *
 * El backend habla en mayúsculas —`POR_CONFIRMAR`, `clave-ambigua`— porque es un
 * vocabulario canónico y no debe traducirse en la base. Pero nadie que cobra
 * facturas quiere leer eso. Aquí, y solo aquí, se traduce a lenguaje de persona.
 *
 * Cada entrada trae además el **porqué**: la mitad del valor del producto es que
 * el usuario entienda en qué se apoyó el sistema, no solo qué decidió
 * (specs/000-dominio.md, principio rector).
 */

import {
  AlertTriangle,
  BadgeCheck,
  Ban,
  Bot,
  Brain,
  CheckCircle2,
  CircleHelp,
  Copy,
  FileWarning,
  Gavel,
  Hourglass,
  PlugZap,
  ShieldCheck,
  ShieldOff,
  TriangleAlert,
  UserCheck,
  Users,
  Zap,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

import type {
  RazonRevision,
  EstadoConexion,
  EstadoFactura,
  EstadoHuerfano,
  EstadoMovimiento,
  Origen,
} from "./api/tipos.ts";

/** Paletas semánticas. Cada tono existe como token en `estilos.css`. */
export type Tono = "exito" | "marca" | "aviso" | "riesgo" | "peligro" | "dato" | "humano" | "neutro";

export interface Etiqueta {
  texto: string;
  tono: Tono;
  icono: LucideIcon;
  /** Una frase. Va en tooltips y en las fichas de detalle. */
  explica: string;
}

export const ESTADO_MOVIMIENTO: Record<EstadoMovimiento, Etiqueta> = {
  CONCILIADO: {
    texto: "Cuadrado con su factura",
    tono: "exito",
    icono: ShieldCheck,
    explica: "Sabemos de quién es el pago y contra qué factura va. Nada que revisar.",
  },
  IDENTIFICADO: {
    texto: "Sabemos de quién es",
    tono: "marca",
    icono: BadgeCheck,
    explica: "Sabemos de quién es la plata, pero la factura no se pudo comprobar.",
  },
  POR_CONFIRMAR: {
    texto: "Falta confirmar",
    tono: "aviso",
    icono: Hourglass,
    explica: "Hay una propuesta, pero la evidencia no alcanza para darla por buena sin que la mires.",
  },
  CONFLICTO: {
    texto: "Puede ser de varios",
    tono: "riesgo",
    icono: Users,
    explica: "Dos o más clientes encajan igual de bien. Elegir uno al azar sería inventar.",
  },
  SIN_IDENTIFICAR: {
    texto: "Falta decir de quién es",
    tono: "neutro",
    icono: CircleHelp,
    explica: "Entró plata y nada de lo que tienes registrado la explica.",
  },
  DATO_INVALIDO: {
    texto: "No se pudo leer",
    tono: "peligro",
    icono: FileWarning,
    explica: "Llegaron valores que 4drando no puede leer. Hay que corregirlos donde se originan.",
  },
};

export const ORIGEN: Record<Origen, Etiqueta> = {
  automatico: {
    texto: "Lo resolvió 4drando",
    tono: "dato",
    icono: Bot,
    explica: "4drando tuvo la evidencia necesaria sin preguntar.",
  },
  humano: {
    texto: "Lo decidió alguien",
    tono: "humano",
    icono: UserCheck,
    explica: "Una persona lo decidió. Queda registrado quién y cuándo.",
  },
  "regla-graduada": {
    texto: "Por regla",
    tono: "dato",
    icono: Gavel,
    explica: "Una regla automática lo resolvió. Apagarla lo devuelve a la bandeja.",
  },
  "identidad-aprendida": {
    texto: "Reconocido por pagos anteriores",
    tono: "marca",
    icono: Brain,
    explica: "Reconocido a partir de confirmaciones anteriores de este mismo pagador.",
  },
  pendiente: {
    texto: "Espera decisión",
    tono: "aviso",
    icono: Hourglass,
    explica: "Nadie lo ha decidido todavía.",
  },
  "humano-obsoleto": {
    texto: "Datos cambiaron",
    tono: "peligro",
    icono: AlertTriangle,
    explica: "Alguien lo confirmó, pero los datos cambiaron después. La confirmación ya no vale.",
  },
  "clave-ambigua": {
    texto: "Dos pagos iguales",
    tono: "riesgo",
    icono: Copy,
    explica:
      "Hay otro movimiento idéntico. Aplicar la confirmación guardada podría asignarla al equivocado.",
  },
};

export const ESTADO_HUERFANO: Record<EstadoHuerfano, Etiqueta> = {
  FALTANTE: {
    texto: "No ha llegado al banco",
    tono: "peligro",
    icono: AlertTriangle,
    explica: "Lo registraste como pagado y el banco no muestra ninguna entrada que corresponda.",
  },
  ESPERADO_SIN_BANCO: {
    texto: "Efectivo",
    tono: "neutro",
    icono: Ban,
    explica: "Por su método de pago no debe aparecer en el banco. No es una alerta.",
  },
  PENDIENTE_ANTIGUEDAD: {
    texto: "Anterior a la fecha de revisión",
    tono: "neutro",
    icono: Hourglass,
    explica: "Anterior a la ventana de revisión configurada.",
  },
  REVISADO: {
    texto: "Revisado",
    tono: "neutro",
    icono: ShieldCheck,
    explica: "Alguien lo revisó y dijo por qué. Ya no se avisa.",
  },
};

/** Por qué se da por revisado un faltante (SPEC-029 §13). */
export const RAZON_REVISION: Record<RazonRevision, string> = {
  "llego-por-otro-medio": "Llegó por otro medio",
  "error-de-digitacion": "Fue un error al registrarlo",
  "todavia-no-llega": "Todavía no llega",
};

export const ESTADO_FACTURA: Record<EstadoFactura, Etiqueta> = {
  PAGADA: { texto: "Pagada", tono: "exito", icono: ShieldCheck, explica: "Saldo en cero." },
  PARCIAL: {
    texto: "Parcial",
    tono: "aviso",
    icono: Hourglass,
    explica: "Tiene pagos que el banco confirma, pero todavía queda saldo.",
  },
  PENDIENTE: {
    texto: "Pendiente",
    tono: "neutro",
    icono: CircleHelp,
    explica: "El banco no confirma ningún pago todavía.",
  },
};

/** Fuerza de la coincidencia de nombre, tal como la calcula `core/texto.js`. */
export const GRADO_NOMBRE: Record<string, { texto: string; tono: Tono }> = {
  TOTAL: { texto: "Nombre idéntico", tono: "exito" },
  FUERTE: { texto: "Nombre muy parecido", tono: "marca" },
  DEBIL: { texto: "Nombre apenas parecido", tono: "aviso" },
  NULO: { texto: "El nombre no se parece", tono: "riesgo" },
};

/** Clases por tono, en un solo sitio para que nada se salga del sistema. */
export const CLASES_TONO: Record<
  Tono,
  { fondo: string; texto: string; borde: string; punto: string; tinte: string }
> =
  {
    exito: {
      fondo: "bg-exito-suave",
      texto: "text-exito-tinta",
      borde: "border-exito/25",
      punto: "bg-exito",
      tinte: "text-exito",
    },
    marca: {
      fondo: "bg-marca-suave",
      texto: "text-marca-tinta",
      borde: "border-marca/25",
      punto: "bg-marca",
      tinte: "text-marca",
    },
    aviso: {
      fondo: "bg-aviso-suave",
      texto: "text-aviso-tinta",
      borde: "border-aviso/30",
      punto: "bg-aviso",
      tinte: "text-aviso",
    },
    riesgo: {
      fondo: "bg-riesgo-suave",
      texto: "text-riesgo-tinta",
      borde: "border-riesgo/30",
      punto: "bg-riesgo",
      tinte: "text-riesgo",
    },
    peligro: {
      fondo: "bg-peligro-suave",
      texto: "text-peligro-tinta",
      borde: "border-peligro/30",
      punto: "bg-peligro",
      tinte: "text-peligro",
    },
    dato: {
      fondo: "bg-dato-suave",
      texto: "text-dato-tinta",
      borde: "border-dato/25",
      punto: "bg-dato",
      tinte: "text-dato",
    },
    humano: {
      fondo: "bg-humano-suave",
      texto: "text-humano-tinta",
      borde: "border-humano/25",
      punto: "bg-humano",
      tinte: "text-humano",
    },
    neutro: {
      fondo: "bg-superficie-2",
      texto: "text-tenue",
      borde: "border-borde",
      punto: "bg-tenue-2",
      tinte: "text-tenue-2",
    },
  };

/**
 * El estado de un sistema conectado (SPEC-026).
 *
 * `explica` no es adorno aquí más que en ningún otro sitio: "en silencio" no le
 * dice a nadie qué se está rompiendo. Lo que hay que decir es la consecuencia —
 * que hay pagos conciliados que no están llegando al otro sistema.
 */
export const ESTADO_CONEXION: Record<EstadoConexion, Etiqueta> = {
  AL_DIA: {
    texto: "Al día",
    tono: "exito",
    icono: CheckCircle2,
    explica: "Ha recogido todo lo que había. No hay nada esperándolo.",
  },
  AL_DIA_CON_COLA: {
    texto: "Recogiendo",
    tono: "marca",
    icono: Zap,
    explica:
      "Está leyendo y le quedan pagos por recoger. Es lo normal después de una pausa o al conectar por primera vez.",
  },
  EN_SILENCIO: {
    texto: "En silencio",
    tono: "riesgo",
    icono: TriangleAlert,
    explica:
      "Leía y dejó de hacerlo, y hay pagos esperando. Están cuadrados aquí y no han llegado a tu otro sistema.",
  },
  SIN_ESTRENAR: {
    texto: "Sin estrenar",
    tono: "neutro",
    icono: PlugZap,
    explica: "La credencial existe y todavía no ha recogido ningún pago. No es un fallo.",
  },
  REVOCADA: {
    texto: "Revocada",
    tono: "neutro",
    icono: ShieldOff,
    explica: "Ya no puede entrar. Se sigue mostrando porque explica qué hizo mientras estuvo viva.",
  },
};
